import type { CaravanDb, LocalBlob } from './db'
import { LocalQuotaError } from './repository'
import type { ProcessedImageBundle } from '../../services/imagePipeline'

type StorageEstimate = { quota?: number; usage?: number }
type MediaRepositoryOptions = { estimateStorage?: () => Promise<StorageEstimate> }

export type StoredMediaBundle = ProcessedImageBundle & { confirmedRemote: boolean }

async function estimateBrowserStorage(): Promise<StorageEstimate> {
  const storage: unknown = Reflect.get(navigator, 'storage')
  if (!storage || typeof storage !== 'object') return {}
  const estimate: unknown = Reflect.get(storage, 'estimate')
  if (typeof estimate !== 'function') return {}
  const result: unknown = await Reflect.apply(estimate, storage, [])
  if (!result || typeof result !== 'object') return {}
  const record = result as Record<string, unknown>
  const output: StorageEstimate = {}
  if (typeof record.quota === 'number') output.quota = record.quota
  if (typeof record.usage === 'number') output.usage = record.usage
  return output
}

export class MediaRepository {
  readonly #db: CaravanDb
  readonly #estimateStorage: () => Promise<StorageEstimate>

  constructor(db: CaravanDb, options: MediaRepositoryOptions = {}) {
    this.#db = db
    this.#estimateStorage = options.estimateStorage ?? estimateBrowserStorage
  }

  async storeBundle(photoId: string, bundle: ProcessedImageBundle, createdAt: string): Promise<void> {
    const required = bundle.original.size + bundle.processed.size + bundle.thumbnail.size
    const estimate = await this.#estimateStorage()
    if (estimate.quota !== undefined && estimate.usage !== undefined && estimate.quota - estimate.usage < required) {
      throw new LocalQuotaError()
    }
    const [originalBytes, processedBytes, thumbnailBytes] = await Promise.all([
      readBlob(bundle.original),
      readBlob(bundle.processed),
      readBlob(bundle.thumbnail),
    ])
    const rows: LocalBlob[] = [
      { key: `${photoId}:original`, entityId: photoId, kind: 'original', bytes: originalBytes, mimeType: bundle.original.type, confirmedRemote: false, createdAt },
      { key: `${photoId}:processed`, entityId: photoId, kind: 'processed', bytes: processedBytes, mimeType: bundle.processed.type, confirmedRemote: false, createdAt },
      { key: `${photoId}:thumbnail`, entityId: photoId, kind: 'thumbnail', bytes: thumbnailBytes, mimeType: bundle.thumbnail.type, confirmedRemote: false, createdAt },
    ]
    try {
      await this.#db.transaction('rw', this.#db.blobs, async () => {
        if (await this.#db.blobs.where('entityId').equals(photoId).count()) {
          throw new Error('Les médias de cette photo existent déjà.')
        }
        await this.#db.blobs.bulkAdd(rows)
      })
    } catch (error) {
      if (isQuotaError(error)) throw new LocalQuotaError()
      throw error
    }
  }

  async getBundle(photoId: string): Promise<StoredMediaBundle | null> {
    const rows = await this.#db.blobs.where('entityId').equals(photoId).toArray()
    const original = rows.find((row) => row.kind === 'original')
    const processed = rows.find((row) => row.kind === 'processed')
    const thumbnail = rows.find((row) => row.kind === 'thumbnail')
    if (!original || !processed || !thumbnail) return null
    const entity = await this.#db.entities.get(`photo:${photoId}`) ?? await this.#db.entities.get(`poll:${photoId}`)
    const metadata = entity?.payload.imageMetadata
    const originalBlob = new Blob([original.bytes], { type: original.mimeType })
    const processedBlob = new Blob([processed.bytes], { type: processed.mimeType })
    const thumbnailBlob = new Blob([thumbnail.bytes], { type: thumbnail.mimeType })
    return {
      original: originalBlob,
      processed: processedBlob,
      thumbnail: thumbnailBlob,
      confirmedRemote: rows.every((row) => row.confirmedRemote),
      metadata: isImageMetadata(metadata)
        ? metadata
        : {
            inputMime: original.mimeType as ProcessedImageBundle['metadata']['inputMime'],
            outputMime: processed.mimeType as ProcessedImageBundle['metadata']['outputMime'],
            inputBytes: original.bytes.byteLength,
            outputBytes: processed.bytes.byteLength,
            thumbnailBytes: thumbnail.bytes.byteLength,
            width: 0,
            height: 0,
          },
    }
  }

  async markConfirmedRemote(photoId: string): Promise<void> {
    await this.#db.blobs.where('entityId').equals(photoId).modify({ confirmedRemote: true })
  }

  async deleteConfirmedOriginal(photoId: string, explicitlyRequested: boolean): Promise<void> {
    if (!explicitlyRequested) throw new Error('La suppression doit être explicitement demandée.')
    const original = await this.#db.blobs.get(`${photoId}:original`)
    if (!original?.confirmedRemote) {
      throw new Error('La confirmation distante est requise avant de supprimer l’original local.')
    }
    await this.#db.blobs.delete(original.key)
  }

  async deleteUncommittedBundle(photoId: string): Promise<void> {
    const entity = await this.#db.entities.get(`photo:${photoId}`)
    if (entity) throw new Error('Une contribution enregistrée ne peut pas être purgée silencieusement.')
    await this.#db.blobs.where('entityId').equals(photoId).delete()
  }
}

function readBlob(blob: Blob): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as ArrayBuffer)
    reader.onerror = () => reject(new Error('La copie locale de cette image a échoué.'))
    reader.readAsArrayBuffer(blob)
  })
}

function isQuotaError(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false
  const record = value as Record<string, unknown>
  return record.name === 'QuotaExceededError' || (typeof record.message === 'string' && /quota|storage full/iu.test(record.message))
}

function isImageMetadata(value: unknown): value is ProcessedImageBundle['metadata'] {
  if (!value || typeof value !== 'object') return false
  const candidate = value as Record<string, unknown>
  return typeof candidate.inputMime === 'string' && typeof candidate.outputMime === 'string' && typeof candidate.width === 'number'
}
